/**
 * Service de détection de la qualité de connexion réseau
 * Inspiré de l'approche YouTube pour adapter le chargement à la connexion
 */

export type ConnectionQuality = 'excellent' | 'good' | 'moderate' | 'slow' | 'offline';

export interface NetworkInfo {
  quality: ConnectionQuality;
  effectiveType: string;
  downlink: number; // Mbps
  rtt: number; // Round-trip time en ms
  saveData: boolean;
  isOnline: boolean;
}

// Étendre l'interface Navigator pour inclure la Network Information API
declare global {
  interface Navigator {
    connection?: {
      effectiveType: string;
      downlink: number;
      rtt: number;
      saveData: boolean;
      addEventListener: (type: string, listener: () => void) => void;
      removeEventListener: (type: string, listener: () => void) => void;
    };
  }
}

class NetworkQualityServiceClass {
  private listeners: Set<(info: NetworkInfo) => void> = new Set();
  private currentInfo: NetworkInfo;
  private measurementHistory: number[] = [];
  private readonly MAX_HISTORY = 5;

  constructor() {
    this.currentInfo = this.detectNetworkQuality();
    this.setupListeners();
  }

  /**
   * Détecte la qualité actuelle de la connexion
   */
  private detectNetworkQuality(): NetworkInfo {
    const isOnline = navigator.onLine;
    
    if (!isOnline) {
      return {
        quality: 'offline',
        effectiveType: 'none',
        downlink: 0,
        rtt: Infinity,
        saveData: false,
        isOnline: false
      };
    }

    // Utiliser Network Information API si disponible
    const connection = navigator.connection;
    
    if (connection) {
      const { effectiveType, downlink, rtt, saveData } = connection;
      
      return {
        quality: this.calculateQuality(effectiveType, downlink, rtt),
        effectiveType: effectiveType || 'unknown',
        downlink: downlink || 10,
        rtt: rtt || 50,
        saveData: saveData || false,
        isOnline: true
      };
    }

    // Fallback: estimation basée sur des mesures
    return {
      quality: 'good',
      effectiveType: 'unknown',
      downlink: 10,
      rtt: 100,
      saveData: false,
      isOnline: true
    };
  }

  /**
   * Calcule la qualité basée sur les métriques réseau
   */
  private calculateQuality(effectiveType: string, downlink: number, rtt: number): ConnectionQuality {
    // D'abord vérifier le type de connexion effectif
    if (effectiveType === '4g' && downlink >= 10 && rtt <= 50) {
      return 'excellent';
    }
    
    if (effectiveType === '4g' || (downlink >= 5 && rtt <= 100)) {
      return 'good';
    }
    
    if (effectiveType === '3g' || (downlink >= 1 && rtt <= 300)) {
      return 'moderate';
    }
    
    if (effectiveType === '2g' || effectiveType === 'slow-2g') {
      return 'slow';
    }

    // Basé sur les mesures numériques
    if (downlink >= 10 && rtt <= 50) return 'excellent';
    if (downlink >= 5 && rtt <= 100) return 'good';
    if (downlink >= 1 && rtt <= 300) return 'moderate';
    return 'slow';
  }

  /**
   * Configure les écouteurs d'événements réseau
   */
  private setupListeners(): void {
    // Écouter les changements online/offline
    window.addEventListener('online', () => this.handleNetworkChange());
    window.addEventListener('offline', () => this.handleNetworkChange());

    // Écouter les changements de qualité de connexion (si supporté)
    if (navigator.connection) {
      navigator.connection.addEventListener('change', () => this.handleNetworkChange());
    }

    // Mesure périodique de la latence
    this.startLatencyMeasurement();
  }

  /**
   * Mesure périodique de la latence pour une estimation plus précise
   */
  private startLatencyMeasurement(): void {
    const measureLatency = async () => {
      if (!navigator.onLine) return;

      const start = performance.now();
      try {
        // Utiliser une ressource légère pour mesurer
        await fetch('/favicon.ico', { 
          method: 'HEAD',
          cache: 'no-store'
        });
        const latency = performance.now() - start;
        
        this.measurementHistory.push(latency);
        if (this.measurementHistory.length > this.MAX_HISTORY) {
          this.measurementHistory.shift();
        }

        // Mettre à jour la qualité si nécessaire
        this.updateQualityFromMeasurements();
      } catch {
        // Erreur réseau, ne rien faire
      }
    };

    // Mesurer toutes les 30 secondes
    setInterval(measureLatency, 30000);
    // Première mesure après 5 secondes
    setTimeout(measureLatency, 5000);
  }

  /**
   * Met à jour la qualité basée sur l'historique des mesures
   */
  private updateQualityFromMeasurements(): void {
    if (this.measurementHistory.length < 2) return;

    const avgLatency = this.measurementHistory.reduce((a, b) => a + b, 0) / this.measurementHistory.length;
    
    let quality: ConnectionQuality;
    if (avgLatency < 100) quality = 'excellent';
    else if (avgLatency < 200) quality = 'good';
    else if (avgLatency < 500) quality = 'moderate';
    else quality = 'slow';

    if (quality !== this.currentInfo.quality) {
      this.currentInfo = { ...this.currentInfo, quality, rtt: avgLatency };
      this.notifyListeners();
    }
  }

  /**
   * Gère les changements de réseau
   */
  private handleNetworkChange(): void {
    const newInfo = this.detectNetworkQuality();
    
    if (JSON.stringify(newInfo) !== JSON.stringify(this.currentInfo)) {
      this.currentInfo = newInfo;
      this.notifyListeners();
    }
  }

  /**
   * Notifie tous les listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentInfo));
  }

  /**
   * Obtient les informations réseau actuelles
   */
  getNetworkInfo(): NetworkInfo {
    return { ...this.currentInfo };
  }

  /**
   * Obtient la qualité de connexion actuelle
   */
  getQuality(): ConnectionQuality {
    return this.currentInfo.quality;
  }

  /**
   * Vérifie si l'utilisateur est en ligne
   */
  isOnline(): boolean {
    return this.currentInfo.isOnline;
  }

  /**
   * Vérifie si le mode économie de données est activé
   */
  isSaveDataMode(): boolean {
    return this.currentInfo.saveData;
  }

  /**
   * S'abonne aux changements de réseau
   */
  subscribe(listener: (info: NetworkInfo) => void): () => void {
    this.listeners.add(listener);
    // Appeler immédiatement avec l'état actuel
    listener(this.currentInfo);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Recommandations de chargement basées sur la qualité
   */
  getLoadingRecommendations() {
    const { quality, saveData } = this.currentInfo;

    return {
      // Nombre d'éléments à charger en parallèle
      parallelLoads: quality === 'excellent' ? 6 : quality === 'good' ? 4 : quality === 'moderate' ? 2 : 1,
      
      // Délai avant de charger les éléments non-critiques (ms)
      deferDelay: quality === 'excellent' ? 0 : quality === 'good' ? 100 : quality === 'moderate' ? 300 : 500,
      
      // Charger les images de haute qualité
      loadHighQualityImages: quality === 'excellent' && !saveData,
      
      // Activer les animations
      enableAnimations: quality !== 'slow' && quality !== 'offline' && !saveData,
      
      // Précharger les données
      enablePrefetch: quality === 'excellent' || quality === 'good',
      
      // Activer le mode réduit
      reducedMode: quality === 'slow' || quality === 'offline' || saveData,
      
      // Taille des chunks de données
      chunkSize: quality === 'excellent' ? 50 : quality === 'good' ? 30 : quality === 'moderate' ? 20 : 10
    };
  }
}

export const NetworkQualityService = new NetworkQualityServiceClass();
